/**
 * PlaybackViewer.jsx
 * 
 * Full interview coding session replay viewer.
 * 
 * Features:
 * - Play/pause/seek through recorded coding sessions
 * - Scrub timeline with visual markers
 * - Speed controls (0.5x, 1x, 2x, 4x)
 * - Jump to run events
 * - Event timeline markers (code runs, language changes, etc.)
 * - Reconstructs editor state from keystroke events
 * - Read-only Monaco editor for playback
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { API_BASE } from '../../utils/config';

// ====== LANGUAGE CONFIG ======
const LANGUAGE_MAP = {
  javascript: 'javascript',
  python: 'python',
  java: 'java',
  cpp: 'cpp',
  sql: 'sql',
};

// ====== STYLES ======
const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#1e1e1e',
    border: '1px solid var(--border-color, #e2e8f0)',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    background: '#252526',
    borderBottom: '1px solid #3c3c3c',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  editorWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'row',
  },
  editorContainer: {
    flex: 1,
    minWidth: 0,
    borderRight: '1px solid #3c3c3c',
  },
  auditSidebar: {
    width: '280px',
    backgroundColor: '#1e1e1e',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  auditHeader: {
    padding: '12px 16px',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '700',
    color: '#94a3b8',
    borderBottom: '1px solid #3c3c3c',
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(30,30,30,0.95)',
    zIndex: 10,
  },
  auditItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #2d2d30',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    transition: 'background 0.2s ease',
  },
  auditTime: {
    fontSize: '11px',
    fontFamily: 'Consolas, monospace',
    color: '#64748b',
  },
  auditLabel: {
    fontSize: '13px',
    color: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  controls: {
    padding: '16px 24px',
    background: 'rgba(30, 30, 30, 0.85)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  timelineContainer: {
    position: 'relative',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    width: '100%',
  },
  timeline: {
    position: 'relative',
    height: '5px',
    width: '100%',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    transition: 'height 0.15s ease',
  },
  timelineHovered: {
    height: '8px',
  },
  timelineProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: '#e50914',
    borderRadius: '4px',
    pointerEvents: 'none',
  },
  scrubberThumb: {
    position: 'absolute',
    top: '50%',
    width: '14px',
    height: '14px',
    backgroundColor: '#e50914',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    boxShadow: '0 0 8px rgba(229,9,20,0.6)',
    transition: 'transform 0.1s ease',
  },
  timelineMarker: {
    position: 'absolute',
    top: '50%',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'pointer',
    zIndex: 2,
    border: '1px solid rgba(0,0,0,0.5)',
  },
  hoverTooltip: {
    position: 'absolute',
    bottom: '24px',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0,0,0,0.85)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Consolas, monospace',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    zIndex: 100,
    border: '1px solid rgba(255,255,255,0.1)',
  },
  controlBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'space-between',
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  controlButton: {
    padding: '8px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    color: '#e2e8f0',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  speedButton: {
    padding: '4px 8px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    background: 'transparent',
    color: '#e2e8f0',
    minWidth: '40px',
    textAlign: 'center',
    transition: 'all 0.15s ease',
  },
  activeSpeed: {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
  },
  timeDisplay: {
    fontSize: '12px',
    color: '#94a3b8',
    fontFamily: 'Consolas, monospace',
    minWidth: '120px',
    textAlign: 'center',
  },
  markerList: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    maxHeight: '60px',
    overflowY: 'auto',
  },
  markerChip: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s ease',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
    color: '#94a3b8',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '12px',
    color: '#64748b',
  },
};

const SPEED_OPTIONS = [0.5, 1, 2, 4];

const formatTime = (ms) => {
  if (!ms || ms < 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const EVENT_COLORS = {
  run_code: '#22c55e',
  language_change: '#f59e0b',
  file_switch: '#3b82f6',
  paste: '#ec4899',
  undo: '#8b5cf6',
  redo: '#6366f1',
  edit: '#475569',
};

/**
 * @param {Object} props
 * @param {string} props.roomId - Interview room ID
 * @param {function} [props.onClose] - Callback when viewer is closed
 */
const PlaybackViewer = ({ roomId, onClose }) => {
  const [events, setEvents] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [currentCode, setCurrentCode] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('javascript');
  const [markers, setMarkers] = useState([]);
  const [isHoveringTimeline, setIsHoveringTimeline] = useState(false);
  const [hoverFraction, setHoverFraction] = useState(0);

  const eventIndexRef = useRef(0);
  const playIntervalRef = useRef(null);
  const startTimeRef = useRef(0);
  const totalDurationRef = useRef(0);
  const currentTimeRef = useRef(0);

  // ====== LOAD EVENTS ======
  useEffect(() => {
    if (!roomId) return;

    const loadReplay = async () => {
      setIsLoading(true);
      try {
        // Load metadata
        const metaRes = await fetch(`${API_BASE}/api/playback/${roomId}/metadata`);
        const metaData = await metaRes.json();
        if (metaData.success && metaData.metadata) {
          setMetadata(metaData.metadata);
        }

        // Load all events
        const eventsRes = await fetch(`${API_BASE}/api/playback/${roomId}/events`);
        const eventsData = await eventsRes.json();

        if (eventsData.success && eventsData.events) {
          const sortedEvents = eventsData.events.sort((a, b) => a.timestamp - b.timestamp);
          setEvents(sortedEvents);

          if (sortedEvents.length > 0) {
            let baseStartTime = sortedEvents[0].timestamp;
            
            // Trim initial silence gap (e.g., waiting 41 mins to start coding)
            const firstInteractiveIdx = sortedEvents.findIndex(e => 
              ['edit', 'run_code', 'language_change', 'insert', 'replace', 'delete', 'paste'].includes(e.type)
            );

            if (firstInteractiveIdx > 0) {
              const interactiveTime = sortedEvents[firstInteractiveIdx].timestamp;
              // If there's more than a 30s gap between join and first action, trim it
              if (interactiveTime - baseStartTime > 30000) {
                baseStartTime = interactiveTime - 5000; // start 5 seconds before the action
              }
            }

            startTimeRef.current = baseStartTime;
            totalDurationRef.current = Math.max(0, sortedEvents[sortedEvents.length - 1].timestamp - baseStartTime);
          }

          // Extract markers (notable events)
          const eventMarkers = sortedEvents
            .filter((e) => ['run_code', 'language_change', 'file_switch'].includes(e.type))
            .map((e) => {
              const relativeTime = e.timestamp - startTimeRef.current;
              return {
                type: e.type,
                time: Math.max(0, relativeTime),
                label:
                  e.type === 'run_code'
                    ? `▶ Run ${e.language || ''}`
                    : e.type === 'language_change'
                    ? `🔀 ${e.language || ''}`
                    : `📁 ${e.fileName || ''}`,
              };
            })
            .filter((m) => m.time >= 0);
          setMarkers(eventMarkers);
        }
      } catch (error) {
        console.error('❌ Failed to load replay data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReplay();
  }, [roomId]);

  // ====== PLAYBACK ENGINE ======
  const processEventsUpTo = useCallback(
    (targetTime) => {
      if (events.length === 0) return;

      const absoluteTarget = startTimeRef.current + targetTime;
      let code = '';
      let lang = 'javascript';

      // Replay all events up to the target time
      for (let i = 0; i < events.length; i++) {
        if (events[i].timestamp > absoluteTarget) break;

        const event = events[i];

        switch (event.type) {
          case 'edit':
          case 'insert':
          case 'replace':
            if (event.content !== undefined) {
              code = event.content;
            } else if (event.text !== undefined) {
              // Simple insert at position
              code = code + event.text;
            }
            break;

          case 'delete':
            if (event.content !== undefined) {
              code = event.content;
            }
            break;

          case 'paste':
            if (event.content !== undefined) {
              code = event.content;
            }
            break;

          case 'undo':
          case 'redo':
            if (event.content !== undefined) {
              code = event.content;
            }
            break;

          case 'language_change':
            lang = event.language || lang;
            if (event.content !== undefined) {
              code = event.content;
            }
            break;

          case 'full_sync':
            code = event.content || code;
            lang = event.language || lang;
            break;

          default:
            break;
        }

        eventIndexRef.current = i;
      }

      setCurrentCode(code);
      setCurrentLanguage(lang);
    },
    [events]
  );

  // ====== PLAY/PAUSE ======
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      // Pause
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
      setIsPlaying(false);
    } else {
      // Play
      setIsPlaying(true);
      
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);

      playIntervalRef.current = setInterval(() => {
        const elapsed = 50 * speed;
        const nextTime = currentTimeRef.current + elapsed;

        if (nextTime >= totalDurationRef.current) {
          // Reached the end
          currentTimeRef.current = totalDurationRef.current;
          setCurrentTime(totalDurationRef.current);
          processEventsUpTo(totalDurationRef.current);
          clearInterval(playIntervalRef.current);
          playIntervalRef.current = null;
          setIsPlaying(false);
          return;
        }

        currentTimeRef.current = nextTime;
        setCurrentTime(nextTime);
        processEventsUpTo(nextTime);
      }, 50); // 50ms tick for smooth playback
    }
  }, [isPlaying, speed, processEventsUpTo]);

  // ====== SEEK / JUMP ENGINE ======
  const executeSeek = useCallback((newTime) => {
    // Because the interval loop strictly adds to `currentTimeRef.current`,
    // we can simply update the ref here. If the video is playing, 
    // the very next 50ms tick will seamlessly continue from this new time!
    currentTimeRef.current = newTime;
    setCurrentTime(newTime);
    processEventsUpTo(newTime);
  }, [processEventsUpTo]);

  // ====== SEEK ======
  const handleSeek = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const fraction = Math.max(0, Math.min(1, x / rect.width));
      const newTime = fraction * totalDurationRef.current;
      executeSeek(newTime);
    },
    [executeSeek]
  );

  // ====== JUMP TO MARKER ======
  const jumpToMarker = useCallback(
    (markerTime) => {
      executeSeek(markerTime);
    },
    [executeSeek]
  );

  // ====== CLEANUP ======
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, []);

  // ====== KEYBOARD SHORTCUTS ======
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          executeSeek(Math.min(totalDurationRef.current, currentTimeRef.current + 5000));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          executeSeek(Math.max(0, currentTimeRef.current - 5000));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSpeed((s) => {
            const idx = SPEED_OPTIONS.indexOf(s);
            return idx < SPEED_OPTIONS.length - 1 ? SPEED_OPTIONS[idx + 1] : s;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSpeed((s) => {
            const idx = SPEED_OPTIONS.indexOf(s);
            return idx > 0 ? SPEED_OPTIONS[idx - 1] : s;
          });
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, executeSeek]);

  // ====== RENDER ======
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #3c3c3c',
              borderTop: '3px solid #6366f1',
              borderRadius: '50%',
              animation: 'pb-spin 0.8s linear infinite',
            }}
          />
          <span>Loading replay data...</span>
          <style>{`@keyframes pb-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <span style={{ fontSize: '48px' }}>📭</span>
          <span style={{ fontSize: '16px', fontWeight: '600' }}>No Replay Data</span>
          <span style={{ fontSize: '13px' }}>No coding events were recorded for this interview session.</span>
          {onClose && (
            <button style={{ ...styles.controlButton, marginTop: '12px' }} onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  const progress = totalDurationRef.current > 0 ? (currentTime / totalDurationRef.current) * 100 : 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <span>🎬</span>
          <span>Interview Replay</span>
          {metadata && (
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '400' }}>
              ({metadata.totalEvents || events.length} events)
            </span>
          )}
        </div>
        {onClose && (
          <button
            style={{ ...styles.controlButton, background: 'transparent', color: '#808080' }}
            onClick={onClose}
          >
            ✕ Close
          </button>
        )}
      </div>

      {/* Editor (read-only playback) & Sidebar */}
      <div style={styles.editorWrapper}>
        <div style={styles.editorContainer}>
          <Editor
            height="100%"
            language={LANGUAGE_MAP[currentLanguage] || 'javascript'}
            value={currentCode}
            theme="vs-dark"
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              wordWrap: 'on',
              renderLineHighlight: 'all',
              padding: { top: 8 },
            }}
          />
        </div>

        {/* Audit Log Sidebar */}
        <div style={styles.auditSidebar}>
          <div style={styles.auditHeader}>Event Audit Log</div>
          {markers.length === 0 ? (
            <div style={{ padding: '20px', color: '#64748b', fontSize: '12px', textAlign: 'center' }}>
              No significant events recorded.
            </div>
          ) : (
            markers.map((marker, idx) => (
              <div 
                key={idx} 
                style={styles.auditItem}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d2d30'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => jumpToMarker(marker.time)}
              >
                <div style={styles.auditLabel}>
                  <div style={{
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: EVENT_COLORS[marker.type] || '#475569',
                    boxShadow: `0 0 6px ${EVENT_COLORS[marker.type] || '#475569'}`
                  }} />
                  {marker.label}
                </div>
                <div style={styles.auditTime}>{formatTime(marker.time)}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Playback Controls */}
      <div style={styles.controls}>
        {/* Timeline */}
        <div 
          style={styles.timelineContainer} 
          onClick={handleSeek}
          onMouseEnter={() => setIsHoveringTimeline(true)}
          onMouseLeave={() => setIsHoveringTimeline(false)}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            setHoverFraction(Math.max(0, Math.min(1, x / rect.width)));
          }}
        >
          <div style={{ ...styles.timeline, ...(isHoveringTimeline ? styles.timelineHovered : {}) }}>
            <div style={{ ...styles.timelineProgress, width: `${progress}%` }} />
            
            {/* Scrubber Thumb */}
            <div style={{
              ...styles.scrubberThumb,
              left: `${progress}%`,
              transform: `translate(-50%, -50%) scale(${isHoveringTimeline ? 1 : 0})`,
            }} />

            {/* Hover Tooltip */}
            {isHoveringTimeline && (
              <div style={{
                ...styles.hoverTooltip,
                left: `${hoverFraction * 100}%`
              }}>
                {formatTime(hoverFraction * totalDurationRef.current)}
              </div>
            )}

            {/* Event markers on timeline */}
            {markers.map((marker, idx) => {
              const markerPos = totalDurationRef.current > 0 ? (marker.time / totalDurationRef.current) * 100 : 0;
              return (
                <div
                  key={idx}
                  style={{
                    ...styles.timelineMarker,
                    left: `${markerPos}%`,
                    backgroundColor: EVENT_COLORS[marker.type] || '#475569',
                    boxShadow: `0 0 6px ${EVENT_COLORS[marker.type] || '#475569'}`
                  }}
                  title={marker.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToMarker(marker.time);
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Control bar */}
        <div style={styles.controlBar}>
          <div style={styles.controlGroup}>
            {/* Play/Pause */}
            <button
              style={styles.controlButton}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={togglePlay}
              title="Play/Pause (Space)"
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Reset */}
            <button
              style={styles.controlButton}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => {
                setCurrentTime(0);
                processEventsUpTo(0);
                eventIndexRef.current = 0;
              }}
              title="Restart"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              </svg>
            </button>

            {/* Time display */}
            <span style={styles.timeDisplay}>
              {formatTime(currentTime)} <span style={{opacity: 0.5}}>/</span> {formatTime(totalDurationRef.current)}
            </span>
          </div>

          <div style={styles.controlGroup}>
            {/* Speed controls */}
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                style={{
                  ...styles.speedButton,
                  ...(speed === s ? styles.activeSpeed : {}),
                }}
                onMouseEnter={(e) => speed !== s && (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)')}
                onMouseLeave={(e) => speed !== s && (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => setSpeed(s)}
                title="Playback Speed (Up/Down Arrow)"
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybackViewer;
